import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function NotificationBell() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);

  const loadCount = useCallback(async () => {
    try { const r = await api.get("/notifications/unread-count"); setCount(r.data.count); } catch {}
  }, [api]);

  useEffect(() => {
    loadCount();
    const interval = setInterval(loadCount, 30000);
    return () => clearInterval(interval);
  }, [loadCount]);

  const handleOpen = async () => {
    if (!open) {
      try { const r = await api.get("/notifications?limit=5"); setItems(r.data); } catch {}
    }
    setOpen(!open);
  };

  const TYPE_LABEL = { broadcast: "Announcement", support_ticket: "Support", intra_org_share: "Shared Search" };

  return (
    <div className="relative" data-testid="notification-bell">
      <button onClick={handleOpen} className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-500 hover:text-slate-700">
        <Bell className="w-5 h-5" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-lime-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notif-badge">{count > 9 ? "9+" : count}</span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-slate-200 shadow-lg z-50 overflow-hidden animate-slideDown" data-testid="notif-dropdown">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <span className="text-sm font-heading text-slate-900">Notifications</span>
              {count > 0 && <button onClick={async () => { await api.put("/notifications/read-all"); setCount(0); }} className="text-xs text-teal-600 font-accent hover:text-teal-700">Mark all read</button>}
            </div>
            <div className="max-h-64 overflow-y-auto">
              {items.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-slate-400">No notifications</p>
              ) : items.map(n => (
                <button key={n.id} onClick={() => { setOpen(false); navigate("/messages"); }}
                  className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${!n.is_read ? "bg-lime-50/40" : ""}`}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <Badge className={`text-[10px] border-0 ${n.communication?.type === "broadcast" ? "bg-blue-100 text-blue-700" : n.communication?.type === "intra_org_share" ? "bg-teal-100 text-teal-700" : "bg-slate-100 text-slate-600"}`}>
                      {TYPE_LABEL[n.communication?.type] || "Message"}
                    </Badge>
                    <span className="text-[10px] text-slate-400 font-mono">{new Date(n.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-slate-600 line-clamp-2">{n.communication?.content?.slice(0, 100)}</p>
                </button>
              ))}
            </div>
            <button onClick={() => { setOpen(false); navigate("/messages"); }} className="w-full px-4 py-2.5 text-center text-xs font-accent text-teal-600 hover:bg-slate-50 border-t border-slate-100">View All Messages</button>
          </div>
        </>
      )}
    </div>
  );
}
